import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { CommandEventType, CommandName, getFragment, isFutureFragment, } from "../../../src/ipm/background";
import { Prefs } from "../../alias/prefs";
import { port } from "../../../adblockplusui/adblockpluschrome/lib/messaging/port";
import { TabSessionStorage } from "../../../adblockplusui/adblockpluschrome/lib/storage/tab-session";
import { EventEmitter } from "../../../adblockplusui/adblockpluschrome/lib/events";
import { getLocaleInfo } from "../../i18n/background";
import * as logger from "../../utilities/background";
import { getSettings } from "../../prefs/background/settings";
import { isCookieBannerDialog, isDialog, isDialogBehavior, isDialogContent } from "./dialog";
import { isPingMessage } from "../shared";
import { setDialogCommandHandler } from "./middleware";
import { clearStats, getStats, setStats } from "./stats";
import { DialogEventType, DialogErrorEventType, ShowOnpageDialogResult, coolDownPeriodKey, lastShownKey, } from "./tab-manager.types";
import { shouldBeDismissed, shouldBeShown, start as setupTimings } from "./timing";
const assignedDialogs = new TabSessionStorage("onpage-dialog:dialogs");
export const eventEmitter = new EventEmitter();
const unassignedDialogs = new Map();
const pagesWithCookieBanner = new Set();
const serverLogger = new ewe.telemetry.ServerLogger("onpage_dialog");
export function compareDialogsByPriority(dialogA, dialogB) {
    var _a, _b;
    if (dialogA.behavior.priority > dialogB.behavior.priority) {
        return -1;
    }
    if (dialogA.behavior.priority < dialogB.behavior.priority) {
        return 1;
    }
    const lowPriorityFallbackValue = "z";
    const ipmIdA = (_a = dialogA.ipmId) !== null && _a !== void 0 ? _a : lowPriorityFallbackValue;
    const ipmIdB = (_b = dialogB.ipmId) !== null && _b !== void 0 ? _b : lowPriorityFallbackValue;
    if (ipmIdA < ipmIdB) {
        return -1;
    }
    if (ipmIdA > ipmIdB) {
        return 1;
    }
    return 0;
}
export async function isCoolDownPeriodOngoing() {
    await Prefs.untilLoaded;
    return Prefs.get(lastShownKey) + Prefs.get(coolDownPeriodKey) > Date.now();
}
function sendMessage(tabId, message) {
    return browser.tabs.sendMessage(tabId, message, { frameId: 0 });
}
function recordLegacyEvent(dialog, eventType) {
    eventEmitter.emit(eventType, dialog);
    if (typeof dialog.ipmId === "string") {
        getFragment().recordEvent(dialog.ipmId, CommandName.createOnPageDialog, eventType);
    }
}
function recordLegacyErrorEvent(eventType) {
    getFragment().recordEvent(null, CommandName.createOnPageDialog, eventType);
}
function dismissDialog(dialog) {
    logger.debug("[onpage-dialog]: Dismiss dialog");
    unassignedDialogs.delete(dialog.id);
    if (typeof dialog.ipmId === "string") {
        getFragment().dismissCommand(dialog.ipmId);
    }
}
async function removeDialogFrame(tabId) {
    const message = { type: "onpage-dialog.hide" };
    await sendMessage(tabId, message);
}
async function removeDialog(tabId, dialog, keepStorage = false) {
    logger.debug("[onpage-dialog]: Remove dialog");
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: Remove dialog, no dialog found");
        return;
    }
    try {
        await removeDialogFrame(tabId);
        await assignedDialogs.delete(tabId);
    }
    catch (ex) {
    }
    if (keepStorage) {
        return;
    }
    const stats = getStats(dialog.id);
    if (!shouldBeDismissed(dialog, stats)) {
        logger.debug("[onpage-dialog]: Keep dialog active");
        return;
    }
    dismissDialog(dialog);
    clearStats(dialog.id);
}
async function handleDialogCommand(ipmId, isInitialization) {
    if (typeof ipmId !== "string") {
        return;
    }
    const { getBehavior, getContent } = getFragment();
    const behavior = getBehavior(ipmId);
    if (!isDialogBehavior(behavior)) {
        return;
    }
    const content = getContent(ipmId);
    if (!isDialogContent(content)) {
        return;
    }
    const dialog = { behavior, content, id: ipmId, ipmId };
    unassignedDialogs.set(dialog.id, dialog);
    if (isFutureFragment()) {
        serverLogger.behavior("command_ready", ipmId, ipmId);
    }
    if (!isInitialization) {
        recordLegacyEvent(dialog, DialogEventType.received);
    }
}
async function handleCloseMessage(message, sender) {
    var _a, _b;
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        return;
    }
    void removeDialog(sender.page.id, dialog);
    if (isFutureFragment()) {
        serverLogger.behavior("dialog_closed", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    }
    recordLegacyEvent(dialog, DialogEventType.closed);
}
async function handleContinueMessage(message, sender) {
    var _a, _b;
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        return;
    }
    const safeTargetUrl = getFragment().createSafeOriginUrl(dialog.behavior.target);
    if (safeTargetUrl === null) {
        return;
    }
    void browser.tabs.create({ url: safeTargetUrl });
    void removeDialog(sender.page.id, dialog);
    if (isFutureFragment()) {
        serverLogger.behavior("dialog_button_clicked", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    }
    recordLegacyEvent(dialog, DialogEventType.buttonClicked);
}
async function handleGetMessage(message, sender) {
    var _a;
    logger.debug("[onpage-dialog]: get");
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return null;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: get, no dialog");
        recordLegacyErrorEvent(DialogErrorEventType.get_no_dialog_found);
        return null;
    }
    return {
        content: dialog.content,
        localeInfo: getLocaleInfo(),
    };
}
async function handlePingMessage(message, sender) {
    var _a, _b;
    if (!isPingMessage(message) || typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: ping message, no dialog");
        recordLegacyErrorEvent(DialogErrorEventType.ping_no_dialog_found);
        return;
    }
    if (dialog.behavior.displayDuration === 0 ||
        message.displayDuration < dialog.behavior.displayDuration) {
        logger.debug("[onpage-dialog]: ping message, duration exceeded");
        return;
    }
    void removeDialog(sender.page.id, dialog);
    if (isFutureFragment()) {
        serverLogger.behavior("dialog_ignored", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    }
    recordLegacyEvent(dialog, DialogEventType.ignored);
}
async function handleErrorMessage(message, sender) {
    var _a;
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!dialog) {
        getFragment().recordEvent(null, CommandName.createOnPageDialog, DialogErrorEventType.error_no_dialog_found);
    }
    getFragment().recordEvent(dialog.ipmId, CommandName.createOnPageDialog, `${message.type}.${message.error}`);
}
function handleTabRemovedEvent(data) {
    var _a;
    const { tabId, value: dialog } = data;
    logger.debug("[onpage-dialog]: Handle Tab Removed Event");
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: Handle Tab Removed Event, no dialog");
        return;
    }
    void removeDialog(tabId, dialog);
    if (isFutureFragment()) {
        serverLogger.behavior("dialog_ignored", (_a = dialog.ipmId) !== null && _a !== void 0 ? _a : "", dialog.ipmId);
    }
    recordLegacyEvent(dialog, DialogEventType.ignored);
}
async function injectDialog(tabId) {
    logger.debug("[onpage-dialog]: Injecting dialog");
    if (browser.scripting) {
        await browser.scripting.insertCSS({
            files: ["adblock-onpage-dialog-user.css"],
            origin: "USER",
            target: { tabId },
        });
        await browser.scripting.executeScript({
            files: ["onpage-dialog.postload.js"],
            target: { tabId },
        });
    }
    else {
        await browser.tabs.insertCSS(tabId, {
            file: "adblock-onpage-dialog-user.css",
            allFrames: false,
            cssOrigin: "user",
            runAt: "document_start",
        });
        await browser.tabs.executeScript(tabId, {
            file: "onpage-dialog.postload.js",
            allFrames: false,
        });
    }
}
async function shouldBeIgnored() {
    if (getSettings().show_survey === false) {
        logger.debug("[onpage-dialog]:show_survey - disabled");
        return true;
    }
    if (getSettings().suppress_surveys) {
        logger.debug("[onpage-dialog]:suppress_surveys - enabled");
        return true;
    }
    if (getSettings().onpageMessages === false) {
        logger.debug("[onpage-dialog]:onpageMessages - disabled");
        return true;
    }
    return false;
}
export async function showOnpageDialog(tabId, tab, dialog) {
    var _a;
    if (typeof dialog.ipmId === "string") {
        void getFragment().checkLanguage(dialog.ipmId);
    }
    if (await shouldBeIgnored()) {
        logger.debug("[onpage-dialog]: User ignores notifications");
        return ShowOnpageDialogResult.rejected;
    }
    if (await isCoolDownPeriodOngoing()) {
        logger.debug("[onpage-dialog]: Cool down period still ongoing");
        return ShowOnpageDialogResult.ignored;
    }
    const stats = getStats(dialog.id);
    const shouldDialogBeDismissed = shouldBeDismissed(dialog, stats);
    const shouldDialogBeShown = await shouldBeShown(tab, dialog, stats);
    if (shouldDialogBeDismissed) {
        logger.debug("[onpage-dialog]: Reject dialog");
        return ShowOnpageDialogResult.rejected;
    }
    if (shouldDialogBeShown && (await assignedDialogs.has(tabId))) {
        logger.debug("[onpage-dialog]: Tab already contains dialog");
        return ShowOnpageDialogResult.rejected;
    }
    if (!shouldDialogBeShown) {
        logger.debug("[onpage-dialog]: Don't show");
        return ShowOnpageDialogResult.ignored;
    }
    if (isCookieBannerDialog(dialog) && !pagesWithCookieBanner.has(tabId)) {
        logger.debug("[onpage-dialog]: No cookie banner detected on page");
        return ShowOnpageDialogResult.ignored;
    }
    logger.debug("[onpage-dialog]: Show dialog");
    await assignedDialogs.set(tabId, dialog);
    const assignedDialogOnPage = await assignedDialogs.get(tabId);
    if (!isDialog(assignedDialogOnPage)) {
        logger.debug("[onpage-dialog]: Failed to be assigned to the page ", tabId);
        return ShowOnpageDialogResult.ignored;
    }
    const now = Date.now();
    setStats(dialog.id, {
        displayCount: stats.displayCount + 1,
        lastDisplayTime: now,
    });
    await Prefs.set(lastShownKey, now);
    try {
        await injectDialog(tabId);
    }
    catch (ex) {
        logger.reportAndLogError(ex, "[onpage-dialog] Injection of dialog failed");
        recordLegacyEvent(dialog, DialogEventType.injected_error);
        return ShowOnpageDialogResult.error;
    }
    if (isFutureFragment()) {
        serverLogger.behavior("dialog_injected", (_a = dialog.ipmId) !== null && _a !== void 0 ? _a : "", dialog.ipmId);
    }
    recordLegacyEvent(dialog, DialogEventType.injected);
    return ShowOnpageDialogResult.shown;
}
function isTabPage(candidate) {
    return (candidate !== null && typeof candidate === "object" && "id" in candidate && "url" in candidate);
}
export async function showDialogFromList(dialogs, tab, dismissDialogFn = dismissDialog) {
    if (!tab.id) {
        return;
    }
    const { doesLicenseStateMatch, getCommand, isCommandExpired } = getFragment();
    for (const dialog of dialogs) {
        if (!(await doesLicenseStateMatch(dialog.behavior))) {
            logger.debug("[onpage-dialog]: License has mismatch");
            dismissDialogFn(dialog);
            continue;
        }
        if (typeof dialog.ipmId === "string") {
            const command = getCommand(dialog.ipmId);
            if (command && isCommandExpired(command)) {
                logger.error("[onpage-dialog]: Command has expired.");
                recordLegacyEvent(dialog, CommandEventType.expired);
                dismissDialogFn(dialog);
                continue;
            }
        }
        if (isCookieBannerDialog(dialog)) {
            const subs = (await SubscriptionAdapter.getSubscriptionsMinusText());
            const cookieSubsbscriptionId = "cookies-premium";
            const isCookieSubscriptionActive = Object.keys(subs).includes(cookieSubsbscriptionId);
            if (isCookieSubscriptionActive) {
                logger.debug("[onpage-dialog]: Dismiss 'cookie banner' OPD due to Premium cookie subscription active");
                dismissDialogFn(dialog);
                continue;
            }
        }
        const result = await showOnpageDialog(tab.id, tab, dialog);
        if (result === ShowOnpageDialogResult.rejected) {
            dismissDialogFn(dialog);
        }
    }
}
async function handlePageLoadedEvent(page) {
    if (!isTabPage(page)) {
        return;
    }
    if (!page.id) {
        return;
    }
    try {
        if (await assignedDialogs.has(page.id)) {
            logger.debug("[onpage-dialog]: Tab already contains dialog");
            return;
        }
        await removeDialogFrame(page.id);
    }
    catch (ex) {
    }
    if (unassignedDialogs.size === 0) {
        logger.debug("[onpage-dialog]: No command");
        return;
    }
    let tab = null;
    try {
        tab = await browser.tabs.get(page.id);
    }
    catch (ex) {
        return;
    }
    if (tab.incognito || typeof tab.url !== "string" || !/^https?:/.test(tab.url)) {
        return;
    }
    const dialogs = Array.from(unassignedDialogs.values()).sort(compareDialogsByPriority);
    void showDialogFromList(dialogs, tab);
}
async function handleCookieBannerDetected(message, sender, processCookieBannerDialogs = showDialogFromList) {
    logger.debug("[onpage-dialog]: Cookie banner detected");
    const { page } = sender;
    let tab = null;
    try {
        tab = await browser.tabs.get(page.id);
    }
    catch (ex) {
        return;
    }
    if (!tab) {
        return;
    }
    if (!tab.id) {
        return;
    }
    pagesWithCookieBanner.add(tab.id);
    let dialogs = Array.from(unassignedDialogs.values()).sort(compareDialogsByPriority);
    dialogs = dialogs.filter((dialog) => isCookieBannerDialog(dialog));
    void processCookieBannerDialogs(dialogs, tab);
}
async function clearCommands() {
    unassignedDialogs.clear();
    const tabs = await browser.tabs.query({});
    for (const tab of tabs) {
        if (typeof tab.id === "undefined") {
            continue;
        }
        const dialog = await assignedDialogs.get(tab.id);
        await removeDialog(tab.id, dialog, true);
    }
}
export async function start() {
    await setupTimings();
    port.on("onpage-dialog.close", handleCloseMessage);
    port.on("onpage-dialog.continue", handleContinueMessage);
    port.on("onpage-dialog.get", handleGetMessage);
    port.on("onpage-dialog.ping", handlePingMessage);
    port.on("onpage-dialog.error", handleErrorMessage);
    port.on("cookie-banner.detected", handleCookieBannerDetected);
    ext.addTrustedMessageTypes(null, [
        "onpage-dialog.continue",
        "onpage-dialog.close",
        "onpage-dialog.get",
        "onpage-dialog.ping",
        "onpage-dialog.error",
        "cookie-banner.detected",
    ]);
    assignedDialogs.on("tab-removed", handleTabRemovedEvent);
    ext.pages.onRemoved.addListener((tabId) => {
        if (pagesWithCookieBanner.has(tabId)) {
            pagesWithCookieBanner.delete(tabId);
        }
    });
    ext.pages.onLoaded.addListener(handlePageLoadedEvent);
    setDialogCommandHandler(handleDialogCommand, clearCommands);
}
